import { z } from 'zod';
/**
 * Kho tài liệu dự án — APM-703, spec mục 7.
 *
 * Hai tính chất định hình mọi schema dưới đây:
 *
 * 1. **Version là bất biến.** Sửa nội dung = thêm version, không phải sửa chỗ
 *    cũ. Vì vậy không có `UpdateDocumentContentInput` — chỉ có
 *    `AddDocumentVersionInput`.
 * 2. **Trạng thái duyệt thuộc về tài liệu**, không thuộc từng version: câu hỏi
 *    người dùng hỏi là "bản SRS này duyệt chưa", không phải "v3 duyệt chưa".
 */
export const DocumentKind = z.enum([
    'srs',
    'design',
    'test_plan',
    'meeting_minutes',
    'contract',
    'other',
]);
export const DocumentStatus = z.enum(['draft', 'review', 'approved']);
/**
 * Tình trạng trích text từ tệp của một version — APM-DOC-c.
 *
 * `null` (vắng mặt) = version không có tệp, không có gì để trích.
 *
 * `skipped` và `failed` cố ý tách nhau: chúng dẫn tới hai câu khác nhau cho
 * người dùng. `skipped` là "ta không đọc được loại này, hoặc tệp quá lớn" —
 * thử lại vô ích. `failed` là "giải mã không ra" — tải lên bản khác thì được.
 * Gộp thành một cờ `extracted: false` là bắt người dùng tự đoán mình nên làm gì.
 */
export const DocumentExtractionStatus = z.enum(['pending', 'extracted', 'skipped', 'failed']);
/** Trần dung lượng tệp còn trích được text — luật #3, xem CLAUDE.md. */
export const MAX_EXTRACT_BYTES = 10 * 1024 * 1024;
/** Trần độ dài nội dung markdown. Body parser giới hạn 1MB, đây là trần nghiệp vụ. */
export const MAX_DOCUMENT_CONTENT = 200_000;
const Content = z.string().max(MAX_DOCUMENT_CONTENT);
export const CreateDocumentInput = z
    .object({
    title: z.string().trim().min(1).max(300),
    kind: DocumentKind.optional(),
    /** Điền sẵn nội dung từ một khuôn dựng sẵn. */
    templateId: z.uuid().optional(),
    content: Content.optional(),
    /** Tệp đã tải lên xong (`POST /files/presign` → `PUT` → `POST /files/:id/commit`). */
    fileId: z.uuid().optional(),
    taskId: z.uuid().optional(),
})
    .superRefine((value, ctx) => {
    if (!value.content && !value.fileId && !value.templateId) {
        // Một tài liệu không có version đầu tiên thì không phải tài liệu. Ba
        // đường vào: gõ nội dung, tải tệp lên, hoặc bắt đầu từ khuôn.
        ctx.addIssue({
            code: 'custom',
            path: ['content'],
            message: 'Cần nội dung, tệp đính kèm, hoặc một khuôn dựng sẵn',
        });
    }
});
/**
 * Sửa **vỏ** tài liệu, không đụng nội dung.
 *
 * Nội dung chỉ đổi bằng cách thêm version — đó là điều kiện để version history
 * có nghĩa gì (spec mục 7).
 */
export const UpdateDocumentInput = z.object({
    title: z.string().trim().min(1).max(300).optional(),
    kind: DocumentKind.optional(),
    /** `null` = gỡ khỏi task. */
    taskId: z.uuid().nullable().optional(),
});
export const AddDocumentVersionInput = z
    .object({
    content: Content.optional(),
    fileId: z.uuid().optional(),
    /** "Đổi gì so với bản trước" — thứ người duyệt đọc đầu tiên. */
    changeNote: z.string().max(500).optional(),
})
    .superRefine((value, ctx) => {
    if (!value.content && !value.fileId) {
        ctx.addIssue({ code: 'custom', path: ['content'], message: 'Version phải có nội dung' });
    }
});
/**
 * "Lưu" trong hộp soạn — APM-PDOC-f. Server **ghi đè** tệp của bản mới nhất khi
 * được phép, không thì thêm phiên bản (`SaveDocumentResult.outcome`).
 */
export const SaveDocumentInput = z.object({
    fileId: z.uuid(),
    /**
     * Bản hộp soạn đang đứng trên (bản đã mở, hoặc bản vừa lưu). Ai đó vừa thêm
     * bản sau nó thì lần lưu này thành phiên bản mới thay vì đè lên bản của họ.
     */
    baseVersionNo: z.number().int().positive(),
    /** Chỉ dùng khi lần lưu thành phiên bản mới. */
    changeNote: z.string().max(500).optional(),
});
export const ReviewDocumentInput = z.object({
    /**
     * Bắt buộc khi trả lại bản nháp: người viết cần biết phải sửa gì. Lúc duyệt
     * thì tuỳ chọn.
     */
    note: z.string().max(1_000).optional(),
});
// ── View ────────────────────────────────────────────────────────────────────
export const DocumentVersionView = z.object({
    id: z.uuid(),
    versionNo: z.number().int().positive(),
    hasContent: z.boolean(),
    /** File đính kèm của version, nếu có. Tải về qua `GET /files/:id/url`. */
    fileId: z.uuid().nullable(),
    filename: z.string().nullable(),
    sizeBytes: z.number().int().nonnegative().nullable(),
    changeNote: z.string().nullable(),
    createdBy: z.uuid(),
    createdByName: z.string().nullable(),
    createdAt: z.iso.datetime(),
    /** Đúng bản này là bản đã được duyệt gần nhất. */
    isApproved: z.boolean(),
    /**
     * Lượt trích text từ tệp — `null` khi version không có tệp. Giao diện cần nó
     * để phân biệt "đang trích" với "đã bỏ qua": cả hai đều cho `hasContent:
     * false`, và người dùng chờ mãi một thứ sẽ không bao giờ tới.
     */
    extractionStatus: DocumentExtractionStatus.nullable(),
    /** Vì sao `skipped`/`failed`, viết cho người dùng đọc. */
    extractionNote: z.string().nullable(),
});
export const DocumentView = z.object({
    id: z.uuid(),
    projectId: z.uuid(),
    title: z.string(),
    kind: DocumentKind,
    status: DocumentStatus,
    latestVersionNo: z.number().int().nonnegative(),
    /** Bản đã duyệt gần nhất — còn nguyên kể cả khi bản mới đang là `draft`. */
    approvedVersionNo: z.number().int().positive().nullable(),
    approvedBy: z.uuid().nullable(),
    approvedAt: z.iso.datetime().nullable(),
    taskId: z.uuid().nullable(),
    taskKey: z.string().nullable(),
    createdBy: z.uuid(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
export const DocumentDetail = DocumentView.extend({
    versions: z.array(DocumentVersionView),
    /** Nội dung của version mới nhất. `null` khi version đó chỉ có tệp. */
    latestContent: z.string().nullable(),
});
/**
 * Kết cục của "Lưu": `revised` = bản mới nhất đã được thay tệp; `new_version` =
 * thêm phiên bản (tài liệu đang chờ duyệt / đã duyệt, bản trước của người
 * khác, hoặc có bản mới hơn chỗ hộp soạn đang đứng).
 */
export const SaveDocumentOutcome = z.enum(['revised', 'new_version']);
export const SaveDocumentResult = z.object({
    document: DocumentDetail,
    outcome: SaveDocumentOutcome,
});
export const DocumentVersionContent = z.object({
    documentId: z.uuid(),
    versionNo: z.number().int().positive(),
    content: z.string().nullable(),
    fileId: z.uuid().nullable(),
    filename: z.string().nullable(),
});
export const DocumentListItem = DocumentView.extend({
    /**
     * Đoạn trích quanh từ khoá, chỉ có khi danh sách đến từ một lượt tìm kiếm.
     * Không có nó thì kết quả tìm kiếm chỉ là một danh sách tiêu đề, và người
     * dùng phải mở từng cái để biết vì sao nó khớp.
     */
    excerpt: z.string().nullable(),
    /** Version chứa đoạn khớp. Bằng `latestVersionNo` khi không tìm kiếm. */
    matchedVersionNo: z.number().int().positive().nullable(),
    /**
     * Tên tệp của bản mới nhất — để danh sách vẽ icon theo định dạng (APM-DOCORD).
     * `null` = bản mới nhất chỉ có nội dung markdown gõ tay.
     */
    latestFilename: z.string().nullable(),
});
export const DocumentListView = z.object({
    projectId: z.uuid(),
    items: z.array(DocumentListItem),
    /** Truy vấn đã dùng, để màn hình hiện "kết quả cho …". */
    query: z.string().nullable(),
});
/**
 * Sắp lại thứ tự kho tài liệu — APM-DOCORD. `documentIds` là **toàn bộ** danh
 * sách theo thứ tự mới (màn hình chỉ cho sắp khi không lọc/tìm), không phải
 * một cặp "kéo A tới trước B": cả danh sách là thứ người dùng vừa nhìn thấy.
 */
export const ReorderDocumentsInput = z.object({
    documentIds: z.array(z.uuid()).min(1).max(1_000),
});
export const DocumentTemplateView = z.object({
    id: z.uuid(),
    name: z.string(),
    slug: z.string(),
    kind: DocumentKind,
    description: z.string().nullable(),
    content: z.string(),
    /** `true` = bộ dựng sẵn dùng chung, chỉ đọc. */
    isBuiltIn: z.boolean(),
});
//# sourceMappingURL=document.js.map